// src/pages/BestCancerNavigationPlatform.jsx
// Target Keywords: "best cancer navigation platform in the US", "world's best oncology navigation", "top cancer navigation platform"

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ShieldCheck, 
  Award, 
  MapPin, 
  CheckCircle2, 
  ArrowRight, 
  Sparkles, 
  HeartHandshake, 
  Activity, 
  Clock, 
  DollarSign, 
  FileText, 
  ChevronRight, 
  Search,
  Star,
  Users,
  Compass,
  Building2,
  Stethoscope
} from 'lucide-react';
import MetaTags from '@/components/SEO/MetaTags';
import { US_STATES } from '@/data/usStatesData';
import { SITE_ORIGIN } from '@/config/site';

export default function BestCancerNavigationPlatform() {
  const [stateSearch, setStateSearch] = useState('');
  const pageUrl = `${SITE_ORIGIN}/best-cancer-navigation-platform`;

  const filteredStates = US_STATES.filter(s => 
    s.name.toLowerCase().includes(stateSearch.toLowerCase()) || 
    s.abbr.toLowerCase().includes(stateSearch.toLowerCase()) ||
    s.majorCities.some(c => c.toLowerCase().includes(stateSearch.toLowerCase()))
  );

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'MedicalWebPage',
      name: "Best Cancer Navigation Platform in the US | World's Best Oncology Navigation",
      description: "ByOnco is the #1 ranked cancer navigation platform in the United States. We get cancer patients to specialists, the fastest. Connecting cancer patients across all 50 states to premier NCI-designated cancer centers, clinical trials, and 24/7 AI navigation.",
      url: pageUrl,
      about: {
        '@type': 'MedicalCondition',
        name: 'Cancer',
      },
      provider: {
        '@type': 'MedicalOrganization',
        name: 'ByOnco Care Navigation',
        url: SITE_ORIGIN,
        logo: `${SITE_ORIGIN}/byonco-logo.png`,
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.97',
          reviewCount: '3842',
          bestRating: '5',
          worstRating: '1',
        },
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_ORIGIN },
        { '@type': 'ListItem', position: 2, name: 'Best Cancer Navigation Platform', item: pageUrl },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What is the best cancer navigation platform in the US?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'ByOnco is widely recognized as the best cancer navigation platform in the United States, providing comprehensive 50-state coverage, direct matching to 68+ NCI-Designated Comprehensive Cancer Centers, rapid virtual second opinions, and 24/7 conversational voice AI care support.',
          },
        },
        {
          '@type': 'Question',
          name: "Why is ByOnco considered the world's best oncology navigation service?",
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Unlike localized hospital navigators who only recommend their own health system, ByOnco delivers unbiased, nationwide clinical navigation across academic cancer powerhouses including MD Anderson, Memorial Sloan Kettering, Mayo Clinic, Dana-Farber, and Johns Hopkins.',
          },
        },
        {
          '@type': 'Question',
          name: 'Does ByOnco cover all 50 US States?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. ByOnco covers all 50 US States, including California, New York, Texas, Massachusetts, Pennsylvania, Ohio, Indiana, Georgia (Atlanta), Washington (Seattle), North and South Carolina, North and South Dakota, Oregon, and Maryland, along with Washington DC.',
          },
        },
        {
          '@type': 'Question',
          name: 'How does ByOnco reduce wait times for oncology appointments and second opinions?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Through automated electronic health record ingestion, oncologist-curated clinical summaries, and direct institutional referral pipelines, ByOnco reduces the average appointment wait time from 24 days to under 48 hours for second opinions.',
          },
        },
        {
          '@type': 'Question',
          name: 'How much does the ByOnco cancer navigation platform cost?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'ByOnco offers transparent, accessible PRO subscription plans starting at $19.99/month, alongside annual and lifetime options. All members receive complete cancer center matching, 24/7 ByOnco AI companion navigation, clinical report decoding, and access to expedited expert second opinions.',
          },
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-cream-paper text-forest-ink pt-20 pb-24">
      <MetaTags
        title="Best Cancer Navigation Platform in the US | World's Best Oncology Navigation"
        description="Ranked #1 Best Cancer Navigation Platform in the United States. Direct matching to top NCI cancer centers, clinical trials, and 24/7 AI oncology support across all 50 states."
        path="/best-cancer-navigation-platform"
        jsonLd={jsonLd}
      />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-emerald-50/70 via-cream-paper to-cream-paper border-b border-border-mist py-16 px-6 sm:px-12">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-100 text-emerald-900 text-xs sm:text-sm font-medium mb-5 border border-emerald-200/60 shadow-2xs">
            <Award className="w-4 h-4 text-emerald-700" />
            <span>#1 Rated Cancer Navigation Platform in the United States</span>
          </div>

          <h1 className="text-4xl sm:text-6xl font-bold font-serif text-forest-ink tracking-tight mb-5 leading-tight">
            Best Cancer Navigation Platform in the US <br className="hidden sm:inline" />
            <span className="text-emerald-700">World's Best Oncology Navigation</span>
          </h1>

          <p className="text-base sm:text-xl text-charcoal/85 max-w-3xl mx-auto mb-8 leading-relaxed font-sans">
            Guiding patients and families through complex cancer diagnoses with unbiased access to premier NCI-Designated Comprehensive Cancer Centers, precision biomarker matching, expedited second opinions, and 24/7 voice AI companion across all 50 US states.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/get-matched"
              className="w-full sm:w-auto px-8 py-4 rounded-xl bg-forest-ink hover:bg-forest-shadow text-white font-bold text-base transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
            >
              Get Matched to Top US Cancer Centers <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/cancer-centers-by-state"
              className="w-full sm:w-auto px-8 py-4 rounded-xl bg-white hover:bg-emerald-50 text-forest-ink font-semibold text-base transition-all border border-border-mist shadow-xs flex items-center justify-center gap-2"
            >
              Browse All 50 US States
            </Link>
          </div>

          {/* Trust Ratings */}
          <div className="mt-12 pt-8 border-t border-border-mist flex flex-wrap items-center justify-center gap-6 text-xs text-charcoal/70">
            <div className="flex items-center gap-1.5 bg-white px-3.5 py-1.5 rounded-full border border-border-mist shadow-2xs">
              <div className="flex text-amber-500">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <span className="font-bold text-forest-ink text-sm">4.97 / 5.0</span>
              <span>(3,840+ Patient Reviews)</span>
            </div>
            <div className="flex items-center gap-2 bg-white px-3.5 py-1.5 rounded-full border border-border-mist shadow-2xs">
              <ShieldCheck className="w-4 h-4 text-emerald-700" />
              <span className="font-medium text-forest-ink">HIPAA Compliant & Medically Supervised</span>
            </div>
            <div className="flex items-center gap-2 bg-white px-3.5 py-1.5 rounded-full border border-border-mist shadow-2xs">
              <Building2 className="w-4 h-4 text-emerald-700" />
              <span className="font-medium text-forest-ink">68 NCI-Designated Centers Coordinated</span>
            </div>
          </div>
        </div>
      </section>

      {/* Proof Metrics Ribbon */}
      <section className="bg-white border-y border-forest-ink/10 py-8 px-6 sm:px-12">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          <div>
            <div className="text-3xl sm:text-4xl font-bold font-serif text-forest-ink">100%</div>
            <div className="text-xs uppercase tracking-wider text-forest-ink/60 font-semibold mt-1">All 50 US States Covered</div>
          </div>
          <div>
            <div className="text-3xl sm:text-4xl font-bold font-serif text-emerald-600">&lt; 48 Hrs</div>
            <div className="text-xs uppercase tracking-wider text-forest-ink/60 font-semibold mt-1">Second Opinion Turnaround</div>
          </div>
          <div>
            <div className="text-3xl sm:text-4xl font-bold font-serif text-forest-ink">14 Days</div>
            <div className="text-xs uppercase tracking-wider text-forest-ink/60 font-semibold mt-1">Avg. Treatment Wait Time Saved</div>
          </div>
          <div>
            <div className="text-3xl sm:text-4xl font-bold font-serif text-emerald-600">24 / 7</div>
            <div className="text-xs uppercase tracking-wider text-forest-ink/60 font-semibold mt-1">Continuous AI Oncology Support</div>
          </div>
        </div>
      </section>

      {/* Comparison: Why ByOnco is #1 */}
      <section className="max-w-6xl mx-auto px-6 sm:px-12 py-20">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold font-serif text-forest-ink mb-4">
            Why ByOnco is the Leading Cancer Navigation Platform in the US
          </h2>
          <p className="text-forest-ink/70 text-base leading-relaxed">
            Standard hospital navigators are restricted to their own institution's doctors. ByOnco is an independent, patient-first oncology navigation ecosystem that opens doors to the nation's best clinical centers.
          </p>
        </div>

        <div className="overflow-x-auto bg-white rounded-2xl border border-forest-ink/10 shadow-sm">
          <table className="w-full text-left text-sm">
            <thead className="bg-forest-ink text-cream-paper text-xs uppercase tracking-wider">
              <tr>
                <th className="py-4 px-6">Navigation Capability</th>
                <th className="py-4 px-6 bg-emerald-700 font-bold">ByOnco Platform</th>
                <th className="py-4 px-6">Hospital-Employed Navigator</th>
                <th className="py-4 px-6">Self-Navigation</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-forest-ink/10">
              <tr>
                <td className="py-4 px-6 font-semibold text-forest-ink">Provider Network</td>
                <td className="py-4 px-6 bg-emerald-50 text-emerald-950 font-bold">All 68 NCI Comprehensive Cancer Centers Across All 50 States</td>
                <td className="py-4 px-6 text-forest-ink/70">Single Hospital System Only</td>
                <td className="py-4 px-6 text-forest-ink/70">Limited to Personal Phone Calls</td>
              </tr>
              <tr>
                <td className="py-4 px-6 font-semibold text-forest-ink">Appointment Acceleration</td>
                <td className="py-4 px-6 bg-emerald-50 text-emerald-950 font-bold">Direct Clinical Referrals in &lt; 48 Hours</td>
                <td className="py-4 px-6 text-forest-ink/70">Standard Internal Waitlist (3-5 Weeks)</td>
                <td className="py-4 px-6 text-forest-ink/70">Average 24+ Days Waiting</td>
              </tr>
              <tr>
                <td className="py-4 px-6 font-semibold text-forest-ink">Virtual Second Opinions</td>
                <td className="py-4 px-6 bg-emerald-50 text-emerald-950 font-bold">Cross-State Multi-Disciplinary Tumor Boards</td>
                <td className="py-4 px-6 text-forest-ink/70">Rarely Offered / In-Network Only</td>
                <td className="py-4 px-6 text-forest-ink/70">Requires Repetitive In-Person Travel</td>
              </tr>
              <tr>
                <td className="py-4 px-6 font-semibold text-forest-ink">Clinical Trial & Biomarker Match</td>
                <td className="py-4 px-6 bg-emerald-50 text-emerald-950 font-bold">Automated NGS / ctDNA Trial Matching Engine</td>
                <td className="py-4 px-6 text-forest-ink/70">Limited to Hospital's In-House Trials</td>
                <td className="py-4 px-6 text-forest-ink/70">Manual Searching on ClinicalTrials.gov</td>
              </tr>
              <tr>
                <td className="py-4 px-6 font-semibold text-forest-ink">24/7 AI Companion (Vani)</td>
                <td className="py-4 px-6 bg-emerald-50 text-emerald-950 font-bold">Continuous Voice AI for Symptom Triage & Questions</td>
                <td className="py-4 px-6 text-forest-ink/70">Business Hours Only (9am - 5pm M-F)</td>
                <td className="py-4 px-6 text-forest-ink/70">Unverified Google Searches</td>
              </tr>
              <tr>
                <td className="py-4 px-6 font-semibold text-forest-ink">Financial & Insurance Navigation</td>
                <td className="py-4 px-6 bg-emerald-50 text-emerald-950 font-bold">Prior Auth Assistance & Transparent Out-of-Pocket Cost Engine</td>
                <td className="py-4 px-6 text-forest-ink/70">Hospital Billing Dept (Complex)</td>
                <td className="py-4 px-6 text-forest-ink/70">Unexpected Surprise Medical Bills</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* The 6 Pillars of Top Cancer Navigation */}
      <section className="bg-forest-ink/5 py-20 px-6 sm:px-12 border-y border-forest-ink/10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold font-serif text-forest-ink mb-4">
              The 6 Pillars of World's Best Oncology Navigation
            </h2>
            <p className="text-forest-ink/70 text-base">
              Every step of your cancer journey is engineered for clinical accuracy, speed, and compassionate support.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Building2,
                title: 'NCI Comprehensive Center Matching',
                desc: 'Precision matching to the top 1% of oncology centers including MD Anderson, Memorial Sloan Kettering, Mayo Clinic, and Dana-Farber based on your exact tumor pathology.',
              },
              {
                icon: Stethoscope,
                title: 'Expert Multidisciplinary Second Opinions',
                desc: 'Confirm your staging, histology, and treatment options with world-renowned subspecialists before initiating chemotherapy, immunotherapy, or surgery.',
              },
              {
                icon: Activity,
                title: 'Precision Biomarker & Trial Matching',
                desc: 'Our algorithms scan open Phase I-III clinical trials matching your molecular alterations (EGFR, KRAS, BRAF, HER2, BRCA, PD-L1).',
              },
              {
                icon: Sparkles,
                title: 'Vani 24/7 Conversational Voice AI',
                desc: 'Ask questions anytime in natural voice. Vani prepares you for oncologist appointments, tracks treatment side effects, and catches red flags early.',
              },
              {
                icon: DollarSign,
                title: 'Treatment Cost & Insurance Clearances',
                desc: 'Calculate estimated out-of-pocket costs, navigate insurance prior authorizations, and identify co-pay assistance foundation grants.',
              },
              {
                icon: HeartHandshake,
                title: 'Dedicated Human Oncology Navigators',
                desc: 'A dedicated navigator coordinates your medical records, pathology slides, travel logistics, and hospital intake from start to finish.',
              },
            ].map((pillar, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-6 border border-forest-ink/10 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center mb-5">
                  <pillar.icon className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold font-serif text-forest-ink mb-2">{pillar.title}</h3>
                <p className="text-sm text-forest-ink/75 leading-relaxed">{pillar.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* All 50 States Geographic Navigation Directory */}
      <section className="max-w-6xl mx-auto px-6 sm:px-12 py-20">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-forest-ink/5 text-forest-ink/80 text-xs font-semibold mb-3">
            <MapPin className="w-3.5 h-3.5 text-emerald-600" />
            <span>Complete Nationwide US Coverage</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold font-serif text-forest-ink mb-4">
            Top Cancer Navigation Across All 50 US States
          </h2>
          <p className="text-forest-ink/70 text-base leading-relaxed">
            Find accredited NCI Comprehensive Cancer Centers, leading hospital networks, and dedicated patient navigation in your home state:
          </p>

          {/* Search box */}
          <div className="max-w-md mx-auto relative mt-6">
            <Search className="w-5 h-5 absolute left-3.5 top-1/2 -translate-y-1/2 text-forest-ink/40" />
            <input
              type="text"
              placeholder="Search by state (e.g. New York, Texas, California, Ohio)..."
              value={stateSearch}
              onChange={(e) => setStateSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-forest-ink/20 bg-white text-forest-ink text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 shadow-sm"
            />
          </div>
        </div>

        {/* Featured Key States Mentioned */}
        <div className="mb-8">
          <h3 className="text-xs font-bold uppercase tracking-wider text-forest-ink/60 mb-3 text-center sm:text-left">
            High-Volume US Oncology Hubs:
          </h3>
          <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
            {[
              { name: 'California (Cali)', slug: 'california' },
              { name: 'Texas (Houston/Dallas)', slug: 'texas' },
              { name: 'New York (NYC/Albany)', slug: 'new-york' },
              { name: 'Massachusetts (MA / Boston)', slug: 'massachusetts' },
              { name: 'Pennsylvania (Penn)', slug: 'pennsylvania' },
              { name: 'Ohio (Cleveland/Columbus)', slug: 'ohio' },
              { name: 'Indiana (Indianapolis)', slug: 'indiana' },
              { name: 'Georgia (Atlanta)', slug: 'georgia' },
              { name: 'Washington (Seattle)', slug: 'washington' },
              { name: 'North Carolina (NC)', slug: 'north-carolina' },
              { name: 'South Carolina (SC)', slug: 'south-carolina' },
              { name: 'North Dakota', slug: 'north-dakota' },
              { name: 'South Dakota', slug: 'south-dakota' },
              { name: 'Oregon (Portland)', slug: 'oregon' },
              { name: 'Maryland (Baltimore)', slug: 'maryland' },
            ].map((st) => (
              <Link
                key={st.slug}
                to={`/cancer-centers/${st.slug}`}
                className="px-3 py-1.5 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-900 border border-emerald-200 text-xs font-semibold transition-colors flex items-center gap-1"
              >
                <span>{st.name}</span>
                <ChevronRight className="w-3 h-3 text-emerald-600" />
              </Link>
            ))}
          </div>
        </div>

        {/* 50 States Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {filteredStates.map((st) => (
            <Link
              key={st.slug}
              to={`/cancer-centers/${st.slug}`}
              className="p-3 rounded-xl bg-white border border-forest-ink/10 hover:border-emerald-500 hover:shadow-md transition-all text-center group"
            >
              <div className="font-bold text-sm text-forest-ink group-hover:text-emerald-700 transition-colors">
                {st.name}
              </div>
              <div className="text-xs text-forest-ink/50 mt-0.5">
                {st.nciCount > 0 ? `${st.nciCount} NCI Center` : `${st.abbr} Directory`}
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Frequently Asked Questions */}
      <section className="bg-white border-t border-forest-ink/10 py-20 px-6 sm:px-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold font-serif text-forest-ink mb-3">
              Frequently Asked Questions About US Cancer Navigation
            </h2>
            <p className="text-forest-ink/70 text-base">
              Everything you need to know about choosing the best oncology navigation platform for you or your loved one.
            </p>
          </div>

          <div className="space-y-4">
            {[
              {
                q: 'What is the best cancer navigation platform in the US?',
                a: 'ByOnco is ranked the #1 best cancer navigation platform in the US because it provides comprehensive 50-state coverage, direct connections to 68 NCI-Designated Comprehensive Cancer Centers, multidisciplinary second opinions in under 48 hours, and 24/7 conversational voice AI navigation with Vani.',
              },
              {
                q: "Why is ByOnco considered the world's best oncology navigation service?",
                a: "ByOnco is completely unbiased and patient-centered. Unlike single-hospital patient navigators who only recommend their internal physicians, ByOnco conducts objective cross-institutional evaluations across top global and US oncology leaders including MD Anderson, MSKCC, Mayo Clinic, and Dana-Farber.",
              },
              {
                q: 'How does ByOnco cover all 50 states (New York, California, Texas, Washington, etc.)?',
                a: 'ByOnco operates a nationwide network connecting state-level academic health systems, NCI cancer centers, and community oncology alliances across all 50 US states, from New York, Massachusetts, Pennsylvania, Ohio, Indiana, and Georgia (Atlanta) to California, Texas, Oregon, Seattle (Washington), Maryland, and both North and South Dakota.',
              },
              {
                q: 'How does ByOnco expedite cancer appointments and second opinions?',
                a: 'Patients normally face a 24 to 35-day delay waiting for oncology appointments. ByOnco digitizes and standardizes all medical records, pathology blocks, and imaging, then directly interfaces with hospital clinical trial and intake coordinators to fast-track appointments in under 48 to 72 hours.',
              },
              {
                q: 'How does the Vani AI agent work alongside human oncology navigators?',
                a: 'Vani is our proprietary conversational voice AI agent trained on NCCN guidelines and oncology literature. Vani is available 24/7 for empathetic symptom logging, preparing questions for doctor appointments, and medication reminders, while board-certified clinical navigators supervise complex medical decisions.',
              },
            ].map((faq, idx) => (
              <div key={idx} className="bg-cream-paper rounded-2xl p-6 border border-forest-ink/10">
                <h3 className="text-lg font-bold text-forest-ink mb-2">{faq.q}</h3>
                <p className="text-sm text-forest-ink/75 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final Conversion Banner */}
      <section className="max-w-6xl mx-auto px-6 sm:px-12 mt-16">
        <div className="bg-gradient-to-r from-forest-ink to-emerald-950 text-white rounded-3xl p-8 sm:p-14 text-center relative overflow-hidden shadow-xl">
          <div className="max-w-2xl mx-auto relative z-10">
            <h2 className="text-3xl sm:text-4xl font-bold font-serif text-white mb-4">
              Get Started with America's #1 Cancer Navigation Platform
            </h2>
            <p className="text-emerald-100/90 text-base mb-8 leading-relaxed font-sans">
              Connect with leading NCI oncologists, verify treatment protocols, and get 24/7 support today. There is no cost for initial center matching.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/get-matched"
                className="w-full sm:w-auto px-8 py-4 rounded-xl bg-emerald-400 hover:bg-emerald-300 text-forest-ink font-bold text-base transition-colors shadow-lg"
              >
                Get Matched With a Specialist
              </Link>
              <Link
                to="/talk"
                className="w-full sm:w-auto px-8 py-4 rounded-xl bg-white/15 hover:bg-white/25 text-white border border-white/20 font-semibold text-base transition-colors"
              >
                Talk to ByOnco AI Companion
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
